import React, { useEffect, useMemo, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShoppingBag, BookMarked, Lock } from "lucide-react";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?w=800&auto=format&fit=crop";

let stripePromiseCache = null;
function getStripe() {
  if (!stripePromiseCache) {
    stripePromiseCache = api.get("/payments/config").then((r) => loadStripe(r.data.publishable_key));
  }
  return stripePromiseCache;
}

export default function Tracts() {
  const [tracts, setTracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ quantity: 1, name: "", email: "", address: "", phone: "", notes: "" });
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/tracts").then((r) => setTracts(r.data)).catch(() => setTracts([])).finally(() => setLoading(false));
  }, []);

  const openOrder = (t) => {
    setSelected(t);
    setCheckoutClientSecret(null);
    setForm({ quantity: 1, name: "", email: "", address: "", phone: "", notes: "" });
  };

  const closeAll = () => {
    setSelected(null);
    setCheckoutClientSecret(null);
  };

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        tract_id: selected.id,
        quantity: parseInt(form.quantity, 10) || 1,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        address: form.address,
        phone: form.phone,
        notes: form.notes,
      });
      setCheckoutClientSecret(data.client_secret);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const stripePromise = useMemo(() => (checkoutClientSecret ? getStripe() : null), [checkoutClientSecret]);

  return (
    <div className="fade-in">
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-primary/80 font-medium mb-3">Gospel Tracts</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-stone-900">Sow the seed.</h1>
        <p className="mt-4 text-lg text-stone-700 max-w-2xl leading-relaxed">
          Simple, printable gospel tracts to leave in a waiting room, hand a neighbor, or slip into a card. Secure checkout by Stripe.
        </p>
      </header>

      {loading ? (
        <div className="text-stone-500" data-testid="tracts-loading">Loading tracts…</div>
      ) : tracts.length === 0 ? (
        <div className="rounded-md border border-dashed border-stone-300 bg-white p-12 text-center" data-testid="tracts-empty">
          <BookMarked className="h-10 w-10 text-stone-400 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl text-stone-900">Coming soon</h3>
          <p className="mt-2 text-stone-600 max-w-md mx-auto">Our tract catalog is being prepared. Check back shortly.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="tracts-grid">
          {tracts.map((t) => (
            <div key={t.id} data-testid={`tract-card-${t.id}`}
              className="group flex flex-col rounded-md border border-stone-200 bg-white overflow-hidden transition-all hover:-translate-y-1 hover:border-primary">
              <div className="relative h-52 w-full overflow-hidden bg-stone-100">
                <img src={t.image_url || PLACEHOLDER_IMG} alt={t.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
              </div>
              <div className="flex-1 p-6 flex flex-col">
                <h3 className="font-serif text-2xl text-stone-900">{t.title}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed line-clamp-3 flex-1">{t.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <div className="font-medium text-lg text-stone-900" data-testid={`tract-price-${t.id}`}>${t.price.toFixed(2)}</div>
                  <Button onClick={() => openOrder(t)} data-testid={`order-btn-${t.id}`}>
                    <ShoppingBag className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Order
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeAll()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{selected?.title}</DialogTitle>
            <DialogDescription>
              {checkoutClientSecret ? "Complete your secure payment below." : "Enter your details, then continue to secure checkout."}
            </DialogDescription>
          </DialogHeader>

          {!checkoutClientSecret ? (
            <form onSubmit={startCheckout} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="qty">Quantity</Label>
                  <Input id="qty" type="number" min={1} required value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    data-testid="order-quantity" />
                </div>
                <div className="pt-6 text-right">
                  <div className="text-xs text-stone-500">Subtotal (before tax/shipping)</div>
                  <div className="font-medium text-lg" data-testid="order-total">
                    ${selected ? ((parseInt(form.quantity, 10) || 0) * selected.price).toFixed(2) : "0.00"}
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="order-name" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="order-email" />
              </div>
              <div>
                <Label htmlFor="address">Shipping address</Label>
                <Textarea id="address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="order-address" />
                <p className="text-xs text-stone-500 mt-1">You'll also confirm shipping in the payment step.</p>
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="order-phone" />
              </div>
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="order-notes" />
              </div>
              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={closeAll} data-testid="order-cancel">Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="order-continue">
                  <Lock className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  {submitting ? "Preparing…" : "Continue to Payment"}
                </Button>
              </div>
            </form>
          ) : (
            <div data-testid="stripe-embedded">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkoutClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
